import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import * as S from './styles';

import HeaderMinimal from '../../../components/newStudent/HeaderMinimal';
import Divider from '../../../components/common/Divider';
import { AlertBar, AlertWarning } from '../../../components/newStudent/Alert';
import { ButtonSimple } from '../../../components/newStudent/Buttons';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../components/newStudent/ProviderNewStudent';
import { documentServiceNewStudent } from '../../../services/newStudent';
import { addDataCache } from '../../../util/common/appUtil';
import type {
  IDocuments,
  IDocumentsPageData,
  IUniversityDegree,
} from '../../../types/newStudent/documents.interface';
import InputFile from '../DocumentsNewStudent/components/InputFile';
import UniversityDegree from '../DocumentsNewStudent/components/UniversityDegree';
import { ONBOARDING_DOCUMENTS_PENDING_KEY } from '../../../variables/onboarding';

export default function DocumentsOnboardingNewStudent() {
  const [data, setData] = useState<IDocumentsPageData>();
  const [documentOpen, setDocumentOpen] = useState();
  const theme = useTheme();
  const navigate = useNavigate();
  const { setIsLoading, unsignedContracts, student, setStudent } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      const { result } = await documentServiceNewStudent.editCheck();
      if (!result) {
        return;
      }

      localStorage.removeItem(ONBOARDING_DOCUMENTS_PENDING_KEY);

      if (student?.dataStudent) {
        const tmpStudent = {
          ...student,
          dataStudent: {
            ...student.dataStudent,
            shouldCheckDocuments: false,
          },
        };
        setStudent(tmpStudent);
        localStorage.setItem('dataStudent', JSON.stringify(tmpStudent));
      }

      const hasUnsignedContracts =
        localStorage.getItem('unsignedContracts') === 'true' ||
        (unsignedContracts && unsignedContracts.length > 0);

      if (hasUnsignedContracts) {
        navigate('/new/aluno/contratos');
        return;
      }

      navigate('/new/aluno/inicio');
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const { data: documentsData, result } =
          await documentServiceNewStudent.getAll();

        if (result) {
          addDataCache(
            'dataDocuments',
            documentsData,
            '/new/aluno/envio-documentos',
          );
          setData(documentsData);
        }
      } catch (error) {
        throw new Error(String(error));
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, []);

  return (
    <S.Container>
      <HeaderMinimal
        title="Envio de Documentos"
        titlePage="Envio de Documentos"
        subtitle="Este passo é opcional e ajuda a agilizar a análise da sua documentação. Você pode enviar agora ou continuar e fazer isso depois em Documentos."
      />
      <Divider padding />
      <AlertBar
        color={theme.title !== 'dark' ? 'tertiary' : 'white'}
        text="O prazo de análise é de 7 a 20 dias úteis. O envio nesta etapa não é obrigatório."
      />
      <AlertWarning title="Você pode continuar sem enviar documentos agora. O próximo passo é a assinatura do contrato, quando houver pendência." />
      {data &&
        data.documents!.map((item: IDocuments, index: number) => (
          <InputFile
            documentType={item.id}
            index={index}
            key={index}
            data={item}
            open={documentOpen}
            setOpen={setDocumentOpen}
          />
        ))}
      {data?.universityDegree &&
        data.universityDegree.map((item: IUniversityDegree, index: number) => (
          <UniversityDegree key={index} data={item} />
        ))}
      <S.ButtonDiv>
        <ButtonSimple
          color="primary"
          widthSize="large"
          onClick={handleContinue}
        >
          Continuar
        </ButtonSimple>
      </S.ButtonDiv>
    </S.Container>
  );
}
